import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useCloseOnEscape } from '../../../hooks/useCloseOnEscape';
import { resolveBookingGrandTotal } from '../../../utils/bookingAmounts';
import { bookingHasFreeSpaceFee, isApprovedBookingStatus, isPaidBookingPaymentStatus } from '../../../utils/bookingPayment';
import { bookingHasAddOns } from '../utils/bookingAddOns';
import { formatCurrency, formatLocalCalendarYmd, formatTime, parseDateOnly } from '../utils/dateUtils';
import occasiaMark from '../../icons/figma/home-mobile/occasia-mark.svg';
import refreshIcon from '../../icons/figma/home-mobile/refresh.svg';
import menuIcon from '../../icons/figma/home-mobile/menu.svg';
import filterIcon from '../../icons/figma/home-mobile/filter.svg';
import calendarIcon from '../../icons/figma/home-mobile/calendar.svg';
import rotateIcon from '../../icons/figma/home-mobile/rotate-left.svg';
import addIcon from '../../icons/figma/home-mobile/add.svg';
import skilletIcon from '../../icons/figma/home-mobile/skillet.svg';
import closeIcon from '../../icons/figma/home-mobile/close.svg';
import arrowLeftIcon from '../../icons/figma/home-mobile/arrow-left.svg';
import checkboxOnIcon from '../../icons/figma/home-mobile/checkbox-on.svg';
import checkboxOffIcon from '../../icons/figma/home-mobile/checkbox-off.svg';
import tickCircleIcon from '../../icons/figma/home-mobile/tick-circle.svg';
import menuHomeIcon from '../../icons/figma/home-mobile/menu-home.svg';
import menuSettingsIcon from '../../icons/figma/home-mobile/menu-settings.svg';
import menuStarIcon from '../../icons/figma/home-mobile/menu-star.svg';
import menuMessageIcon from '../../icons/figma/home-mobile/menu-message.svg';
import menuLogoutIcon from '../../icons/figma/home-mobile/menu-logout.svg';
import menuCloseIcon from '../../icons/figma/home-mobile/menu-close.svg';
import '../styles/HomeMobileList.css';

const HOME_MOBILE_PAGE_SIZE = 16;

const TABS = {
  upcoming: 'upcoming',
  past: 'past'
};

const EMPTY_FILTERS = {
  accepted: false,
  notAccepted: false,
  paid: false,
  pending: false,
  manual: false,
  platform: false,
  preorder: false,
  freeSpace: false
};

const FILTER_GROUPS = [
  {
    title: 'Booking status',
    options: [
      { key: 'accepted', label: 'Accepted' },
      { key: 'notAccepted', label: 'Not accepted' }
    ]
  },
  {
    title: 'Payment status',
    options: [
      { key: 'paid', label: 'Paid' },
      { key: 'pending', label: 'Pending' }
    ]
  },
  {
    title: 'Booking type',
    options: [
      { key: 'manual', label: 'Manual booking' },
      { key: 'platform', label: 'Platform booking' }
    ]
  },
  {
    title: 'Other',
    options: [
      { key: 'preorder', label: 'Pre-order available' },
      { key: 'freeSpace', label: 'Free space' }
    ]
  }
];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const buildMonthCells = (year, month) => {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = Array.from({ length: first.getDay() }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(year, month, day);
    cells.push({ day, ymd: formatLocalCalendarYmd(date) });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const bookingYmd = (booking) => {
  const raw = booking?.booking_date || booking?.event_date || booking?.date;
  if (!raw) return '';
  return String(raw).split('T')[0];
};

const bookingStatusLabel = (status) => {
  const key = String(status || 'pending').toLowerCase();
  const labels = {
    pending: 'Pending',
    confirmed: 'Accepted',
    completed: 'Completed',
    on_hold: 'On hold',
    canceled_by_user: 'Cancelled',
    canceled_by_owner: 'Cancelled',
    cancelled: 'Cancelled'
  };
  return labels[key] || key.replace(/_/g, ' ');
};

const bookingStatusTone = (status) => {
  const key = String(status || 'pending').toLowerCase();
  if (key === 'confirmed' || key === 'completed' || key === 'accepted') return 'accepted';
  if (key.includes('cancel')) return 'cancelled';
  return 'pending';
};

const paymentLabel = (raw) => {
  const key = String(raw ?? 'pending').toLowerCase();
  if (['completed', 'paid', '1'].includes(key)) return 'Paid';
  if (key === 'refunded') return 'Refunded';
  return 'Pending';
};

const paymentTone = (raw) => {
  const key = String(raw ?? 'pending').toLowerCase();
  if (['completed', 'paid', '1'].includes(key)) return 'paid';
  if (key === 'refunded') return 'cancelled';
  return 'pending';
};

const isManualBooking = (booking) => {
  const source = String(
    booking?.booking_source ?? booking?.source ?? booking?.requirements?.source ?? 'platform'
  ).toLowerCase();
  return source === 'manual' || source === 'venue_dashboard';
};

const matchesChoice = (selected, value) => selected.length === 0 || selected.includes(value);

const matchesFilters = (booking, filters) => {
  const acceptance = [];
  if (filters.accepted) acceptance.push(true);
  if (filters.notAccepted) acceptance.push(false);
  const accepted = isApprovedBookingStatus(booking?.status ?? booking?.booking_status);
  if (!matchesChoice(acceptance, accepted)) return false;

  const payments = [];
  if (filters.paid) payments.push(true);
  if (filters.pending) payments.push(false);
  const paid = isPaidBookingPaymentStatus(booking?.payment_status ?? booking?.paymentStatus);
  if (!matchesChoice(payments, paid)) return false;

  const types = [];
  if (filters.manual) types.push(true);
  if (filters.platform) types.push(false);
  if (!matchesChoice(types, isManualBooking(booking))) return false;

  if (filters.preorder && !bookingHasAddOns(booking)) return false;
  if (filters.freeSpace && !bookingHasFreeSpaceFee(booking)) return false;
  return true;
};

const HomeMobileList = ({
  bookings = [],
  loading = false,
  error = '',
  refreshing = false,
  syncLoading = false,
  onRefresh,
  onSyncCalendar,
  onAddBooking,
  onOpenBooking,
  onSectionChange,
  onEnsurePastWindow,
  onLoadPastBookings,
  loadingPastBookings = false,
  hasMorePastBookings = false
}) => {
  const { logout, isLoggingOut } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(TABS.upcoming);
  const [menuOpen, setMenuOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [jumpYmd, setJumpYmd] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [dateSheetOpen, setDateSheetOpen] = useState(false);
  const [draftYmd, setDraftYmd] = useState('');
  const [viewMonth, setViewMonth] = useState(() => new Date());
  const [syncSheetOpen, setSyncSheetOpen] = useState(false);
  const [syncCount, setSyncCount] = useState(0);
  const [visibleCount, setVisibleCount] = useState(HOME_MOBILE_PAGE_SIZE);

  const todayYmd = formatLocalCalendarYmd(new Date());
  const filtersActive = Object.values(appliedFilters).some(Boolean);

  const groups = useMemo(() => {
    const buckets = new Map();
    bookings.forEach((booking) => {
      const ymd = bookingYmd(booking);
      const isPast = Boolean(ymd) && ymd < todayYmd;
      if (dateFilter) {
        if (ymd !== dateFilter) return;
      } else if (tab === TABS.upcoming ? isPast : !isPast) {
        return;
      }
      if (!matchesFilters(booking, appliedFilters)) return;
      const key = ymd || 'unscheduled';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(booking);
    });

    return [...buckets.entries()]
      .sort((a, b) => {
        if (a[0] === 'unscheduled') return 1;
        if (b[0] === 'unscheduled') return -1;
        return tab === TABS.upcoming ? a[0].localeCompare(b[0]) : b[0].localeCompare(a[0]);
      })
      .map(([ymd, items]) => ({
        ymd,
        items: [...items].sort((a, b) => String(a.start_time || '').localeCompare(String(b.start_time || '')))
      }));
  }, [bookings, tab, todayYmd, appliedFilters, dateFilter]);

  const flatCount = useMemo(
    () => groups.reduce((sum, group) => sum + group.items.length, 0),
    [groups]
  );

  const visibleGroups = useMemo(() => {
    let remaining = visibleCount;
    const next = [];
    for (const group of groups) {
      if (remaining <= 0) break;
      const items = group.items.slice(0, remaining);
      remaining -= items.length;
      next.push({ ...group, items });
    }
    return next;
  }, [groups, visibleCount]);

  useEffect(() => {
    setVisibleCount(HOME_MOBILE_PAGE_SIZE);
  }, [tab, dateFilter, appliedFilters, bookings.length]);

  useEffect(() => {
    if (tab !== TABS.past || !onEnsurePastWindow) return;
    onEnsurePastWindow();
  }, [tab, onEnsurePastWindow]);

  useEffect(() => {
    if (!jumpYmd) return;
    const node = document.getElementById(`home-mobile-day-${jumpYmd}`);
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [jumpYmd, tab, groups]);

  const closeDateSheet = () => setDateSheetOpen(false);
  const closeFilterSheet = () => setFiltersOpen(false);
  const closeSyncSheet = () => setSyncSheetOpen(false);
  const closeMenu = () => setMenuOpen(false);
  useCloseOnEscape(dateSheetOpen, closeDateSheet);
  useCloseOnEscape(filtersOpen, closeFilterSheet);
  useCloseOnEscape(syncSheetOpen, closeSyncSheet);
  useCloseOnEscape(menuOpen, closeMenu);

  useEffect(() => {
    if (!dateSheetOpen && !filtersOpen && !syncSheetOpen && !menuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [dateSheetOpen, filtersOpen, syncSheetOpen, menuOpen]);

  const handleSyncCalendar = async () => {
    if (!onSyncCalendar || syncLoading) return;
    setMenuOpen(false);
    try {
      const result = await onSyncCalendar();
      if (result && typeof result.count === 'number') {
        setSyncCount(result.count);
        setSyncSheetOpen(true);
      }
    } catch {
      // The sync hook already surfaces failures.
    }
  };

  const openDateSheet = () => {
    const seed = dateFilter || todayYmd;
    const parsed = parseDateOnly(seed) || new Date();
    setDraftYmd(seed);
    setViewMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
    setMenuOpen(false);
    setFiltersOpen(false);
    setDateSheetOpen(true);
  };

  const openFilterSheet = () => {
    setDraftFilters(appliedFilters);
    setMenuOpen(false);
    setDateSheetOpen(false);
    setFiltersOpen(true);
  };

  const toggleDraftFilter = (key) => {
    setDraftFilters((current) => ({ ...current, [key]: !current[key] }));
  };

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setFiltersOpen(false);
  };

  const resetFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setFiltersOpen(false);
  };

  const shiftMonth = (delta) => {
    setViewMonth((current) => new Date(current.getFullYear(), current.getMonth() + delta, 1));
  };

  const applyDateFilter = () => {
    if (!draftYmd) return;
    setDateFilter(draftYmd);
    setTab(draftYmd < todayYmd ? TABS.past : TABS.upcoming);
    setJumpYmd(draftYmd);
    setDateSheetOpen(false);
  };

  const selectTab = (nextTab) => {
    setDateFilter('');
    setTab(nextTab);
  };

  const monthCells = buildMonthCells(viewMonth.getFullYear(), viewMonth.getMonth());
  const monthLabel = viewMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/');
    }
  };

  const menuItems = [
    { id: 'home', label: 'Home', icon: menuHomeIcon },
    { id: 'settings', label: 'Settings', icon: menuSettingsIcon },
    { id: 'feedback', label: 'Customer feedback', icon: menuStarIcon },
    { id: 'comms', label: 'Messages', icon: menuMessageIcon }
  ];

  return (
    <div className="home-mobile">
      <header className="home-mobile-header">
        <div className="home-mobile-top">
          <img src={occasiaMark} alt="Occasia" className="home-mobile-logo" width="24" height="24" />
          <div className="home-mobile-top-actions">
            <button
              type="button"
              className="home-mobile-sync"
              onClick={handleSyncCalendar}
              disabled={!onSyncCalendar || syncLoading}
            >
              <img src={refreshIcon} alt="" width="16" height="16" />
              <span>{syncLoading ? 'Syncing...' : 'Sync calendar'}</span>
            </button>
            <button
              type="button"
              className="home-mobile-icon-btn"
              aria-label="Menu"
              aria-expanded={menuOpen}
              onClick={() => {
                setFiltersOpen(false);
                setDateSheetOpen(false);
                setSyncSheetOpen(false);
                setMenuOpen(true);
              }}
            >
              <img src={menuIcon} alt="" width="20" height="20" />
            </button>
          </div>
        </div>
        <div className="home-mobile-toolbar">
          <div className="home-mobile-tabs" role="tablist" aria-label="Booking time">
            <button
              type="button"
              role="tab"
              aria-selected={tab === TABS.upcoming}
              className={`home-mobile-tab${tab === TABS.upcoming ? ' is-active' : ''}`}
              onClick={() => selectTab(TABS.upcoming)}
            >
              Upcoming
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === TABS.past}
              className={`home-mobile-tab${tab === TABS.past ? ' is-active' : ''}`}
              onClick={() => selectTab(TABS.past)}
            >
              Past
            </button>
          </div>
          <div className="home-mobile-toolbar-actions">
            <button
              type="button"
              className={`home-mobile-icon-btn${filtersOpen || filtersActive ? ' is-active' : ''}`}
              aria-label="Filter bookings"
              aria-expanded={filtersOpen}
              onClick={openFilterSheet}
            >
              <img src={filterIcon} alt="" width="20" height="20" />
            </button>
            <button
              type="button"
              className={`home-mobile-icon-btn${dateSheetOpen || dateFilter ? ' is-active' : ''}`}
              aria-label="Select date"
              aria-expanded={dateSheetOpen}
              onClick={openDateSheet}
            >
              <img src={calendarIcon} alt="" width="20" height="20" />
            </button>
            <button
              type="button"
              className="home-mobile-icon-btn"
              aria-label="Refresh bookings"
              onClick={onRefresh}
              disabled={!onRefresh || refreshing}
            >
              <img
                src={rotateIcon}
                alt=""
                width="20"
                height="20"
                className={refreshing ? 'is-spinning' : ''}
              />
            </button>
          </div>
        </div>
      </header>

      <div className="home-mobile-body">
        {loading && bookings.length === 0 ? (
          <p className="home-mobile-status">Loading bookings…</p>
        ) : null}
        {error ? (
          <div className="home-mobile-status">
            <p>{error}</p>
            {onRefresh ? (
              <button type="button" className="home-mobile-retry" onClick={onRefresh}>
                Try again
              </button>
            ) : null}
          </div>
        ) : null}
        {!loading && !error && groups.length === 0 ? (
          <p className="home-mobile-status">
            {dateFilter
              ? 'No bookings on this date'
              : tab === TABS.upcoming
                ? 'No upcoming bookings'
                : 'No past bookings'}
          </p>
        ) : null}
        {visibleGroups.map((group) => {
          const date = group.ymd === 'unscheduled' ? null : parseDateOnly(group.ymd);
          const heading = date
            ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : 'Unscheduled';
          const weekday = date ? date.toLocaleDateString('en-US', { weekday: 'long' }) : '';
          return (
            <section
              key={group.ymd}
              id={group.ymd === 'unscheduled' ? undefined : `home-mobile-day-${group.ymd}`}
              className="home-mobile-day"
            >
              <div className="home-mobile-day-heading">
                <h2>{heading}</h2>
                {weekday ? (
                  <>
                    <span className="home-mobile-day-dot" aria-hidden="true" />
                    <p>{weekday}</p>
                  </>
                ) : null}
              </div>
              <div className="home-mobile-cards">
                {group.items.map((booking) => {
                  const status = booking.status || booking.booking_status || 'pending';
                  const paymentRaw = booking.payment_status ?? booking.paymentStatus ?? 'pending';
                  const total = resolveBookingGrandTotal(booking);
                  const title =
                    booking.event_name || booking.event || booking.eventName || booking.title || 'Booking';
                  const reference = booking.booking_reference || booking.id?.slice?.(-8) || '—';
                  const cardDate = date
                    ? date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                    : '—';
                  const start = formatTime(booking.start_time);
                  const end = formatTime(booking.end_time);
                  const timeLabel = start && end ? `${start} - ${end}` : start || end || '—';
                  const hasFreeSpace = bookingHasFreeSpaceFee(booking);
                  const hasPreorder = bookingHasAddOns(booking);
                  return (
                    <button
                      type="button"
                      key={booking.id || booking.booking_reference || `${group.ymd}-${title}`}
                      className="home-mobile-card"
                      onClick={() => onOpenBooking?.(booking)}
                    >
                      <div className="home-mobile-card-top">
                        <div className="home-mobile-card-title">
                          <p>{title}</p>
                          <span>{reference}</span>
                        </div>
                        <div className="home-mobile-card-price">
                          <p>{Number.isFinite(total) ? formatCurrency(total) : '—'}</p>
                          {hasFreeSpace ? <span>Free Space</span> : null}
                        </div>
                      </div>
                      <div className="home-mobile-card-grid">
                        <div>
                          <span>Date</span>
                          <p>{cardDate}</p>
                        </div>
                        <div>
                          <span>Time</span>
                          <p>{timeLabel}</p>
                        </div>
                      </div>
                      <div className="home-mobile-card-grid home-mobile-card-grid--status">
                        <div>
                          <span>Booking status</span>
                          <em className={`home-mobile-tag home-mobile-tag--${bookingStatusTone(status)}`}>
                            {bookingStatusLabel(status)}
                          </em>
                        </div>
                        <div>
                          <span>Payment status</span>
                          <em className={`home-mobile-tag home-mobile-tag--${paymentTone(paymentRaw)}`}>
                            {paymentLabel(paymentRaw)}
                          </em>
                        </div>
                      </div>
                      {hasPreorder ? (
                        <div className="home-mobile-preorder">
                          <img src={skilletIcon} alt="" width="16" height="16" />
                          <span>Pre-order available</span>
                        </div>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
        {flatCount > visibleCount ? (
          <div className="home-mobile-load-more-row">
            <button
              type="button"
              className="home-mobile-load-more-btn"
              onClick={() =>
                setVisibleCount((count) => Math.min(count + HOME_MOBILE_PAGE_SIZE, flatCount))
              }
            >
              Load more ({flatCount - visibleCount} remaining)
            </button>
          </div>
        ) : null}
        {tab === TABS.past && hasMorePastBookings ? (
          <div className="home-mobile-load-more-row">
            <button
              type="button"
              className="home-mobile-load-more-btn"
              disabled={loadingPastBookings}
              onClick={() => onLoadPastBookings?.()}
            >
              {loadingPastBookings ? 'Loading older bookings…' : 'Load older bookings'}
            </button>
          </div>
        ) : null}
      </div>

      {menuOpen ? (
        <div className="home-mobile-menu-overlay" onClick={closeMenu}>
          <div
            className="home-mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="home-mobile-menu-header">
              <img src={occasiaMark} alt="Occasia" width="24" height="24" />
              <button type="button" className="home-mobile-menu-close" aria-label="Close" onClick={closeMenu}>
                <img src={menuCloseIcon} alt="" width="20" height="20" />
              </button>
            </div>
            {menuItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className="home-mobile-menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  if (item.id !== 'home') onSectionChange?.(item.id);
                }}
              >
                <img src={item.icon} alt="" width="20" height="20" />
                <span>{item.label}</span>
              </button>
            ))}
            <button
              type="button"
              className="home-mobile-menu-item home-mobile-menu-item--logout"
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              <img src={menuLogoutIcon} alt="" width="20" height="20" />
              <span>{isLoggingOut ? 'Signing out...' : 'Logout'}</span>
            </button>
          </div>
        </div>
      ) : null}

      {syncSheetOpen ? (
        <div className="home-mobile-date-overlay" onClick={closeSyncSheet}>
          <div
            className="home-mobile-sync-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="home-mobile-sync-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="home-mobile-sync-body">
              <div className="home-mobile-sync-icon">
                <img src={tickCircleIcon} alt="" width="48" height="48" />
              </div>
              <div className="home-mobile-sync-copy">
                <h2 id="home-mobile-sync-title">Calendar synced!</h2>
                <p>Found {syncCount} busy time(s)</p>
              </div>
            </div>
            <button type="button" className="home-mobile-sync-ok" onClick={closeSyncSheet}>
              Okay, got it
            </button>
          </div>
        </div>
      ) : null}

      {filtersOpen ? (
        <div className="home-mobile-date-overlay" onClick={closeFilterSheet}>
          <div
            className="home-mobile-date-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="home-mobile-filter-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="home-mobile-date-sheet-header">
              <h2 id="home-mobile-filter-title">Filters</h2>
              <button type="button" className="home-mobile-date-close" aria-label="Close" onClick={closeFilterSheet}>
                <img src={closeIcon} alt="" width="20" height="20" />
              </button>
            </div>
            {FILTER_GROUPS.map((group, index) => (
              <div
                key={group.title}
                className={`home-mobile-filter-section${index === FILTER_GROUPS.length - 1 ? ' is-last' : ''}`}
              >
                <h3>{group.title}</h3>
                {group.options.map((option) => {
                  const checked = Boolean(draftFilters[option.key]);
                  return (
                    <button
                      key={option.key}
                      type="button"
                      className="home-mobile-filter-option"
                      aria-pressed={checked}
                      onClick={() => toggleDraftFilter(option.key)}
                    >
                      <span>{option.label}</span>
                      <img src={checked ? checkboxOnIcon : checkboxOffIcon} alt="" width="20" height="20" />
                    </button>
                  );
                })}
              </div>
            ))}
            <button type="button" className="home-mobile-date-apply" onClick={applyFilters}>
              Apply
            </button>
            <button type="button" className="home-mobile-filter-reset" onClick={resetFilters}>
              Reset filters
            </button>
          </div>
        </div>
      ) : null}

      {dateSheetOpen ? (
        <div className="home-mobile-date-overlay" onClick={closeDateSheet}>
          <div
            className="home-mobile-date-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="home-mobile-date-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="home-mobile-date-sheet-header">
              <h2 id="home-mobile-date-title">Select date</h2>
              <button type="button" className="home-mobile-date-close" aria-label="Close" onClick={closeDateSheet}>
                <img src={closeIcon} alt="" width="20" height="20" />
              </button>
            </div>
            <div className="home-mobile-date-month">
              <p>{monthLabel}</p>
              <div className="home-mobile-date-nav">
                <button type="button" aria-label="Previous month" onClick={() => shiftMonth(-1)}>
                  <img src={arrowLeftIcon} alt="" width="20" height="20" />
                </button>
                <button type="button" aria-label="Next month" onClick={() => shiftMonth(1)}>
                  <img src={arrowLeftIcon} alt="" width="20" height="20" className="is-next" />
                </button>
              </div>
            </div>
            <div className="home-mobile-date-weekdays" aria-hidden="true">
              {WEEKDAYS.map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>
            <div className="home-mobile-date-grid">
              {monthCells.map((cell, index) =>
                cell ? (
                  <button
                    key={cell.ymd}
                    type="button"
                    className={`home-mobile-date-day${cell.ymd === draftYmd ? ' is-selected' : ''}`}
                    aria-pressed={cell.ymd === draftYmd}
                    onClick={() => setDraftYmd(cell.ymd)}
                  >
                    {cell.day}
                  </button>
                ) : (
                  <span key={`empty-${index}`} className="home-mobile-date-day is-empty" />
                )
              )}
            </div>
            <button type="button" className="home-mobile-date-apply" onClick={applyDateFilter}>
              Apply
            </button>
          </div>
        </div>
      ) : null}

      <button type="button" className="home-mobile-add" onClick={onAddBooking}>
        <img src={addIcon} alt="" width="16" height="16" />
        <span>Add booking</span>
      </button>
    </div>
  );
};

export default HomeMobileList;
